import React from "react";
import { NavLink } from "react-router-dom";
import { FaBell, FaComment, FaSms } from "react-icons/fa";

// ── Channels — only App Push is wired up on the backend for now ────
export const CHANNEL_OPTIONS = [
    { value: "app_push", label: "App Push", icon: FaBell,    enabled: true },
    { value: "whatsapp", label: "WhatsApp",  icon: FaComment, enabled: false },
    { value: "sms",      label: "SMS",       icon: FaSms,     enabled: false },
];

export const channelLabel = (value) => CHANNEL_OPTIONS.find((c) => c.value === value)?.label || value;

export const ChannelBadge = ({ channel }) => {
    const opt = CHANNEL_OPTIONS.find((c) => c.value === channel);
    const Icon = opt?.icon || FaBell;
    return (
        <span className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full bg-primary/10 text-primary">
            <Icon size={10} />
            {opt?.label || channel}
        </span>
    );
};

// ── Channel tabs — used as the channel filter on Broadcasts & Templates ──
export const ChannelTabs = ({ value, onChange }) => (
    <div className="inline-flex items-center gap-1 bg-white border border-gray-100 shadow-sm rounded-xl p-1">
        {CHANNEL_OPTIONS.map((c) => {
            const active = value === c.value;
            return (
                <button
                    key={c.value}
                    type="button"
                    disabled={!c.enabled}
                    onClick={() => c.enabled && onChange(c.value)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                        active ? "bg-primary text-white shadow-sm" : "text-gray-500 hover:bg-gray-50"
                    } ${!c.enabled ? "opacity-50 cursor-not-allowed hover:bg-transparent" : ""}`}
                >
                    <c.icon size={11} />
                    {c.label}
                    {!c.enabled && (
                        <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${
                            active ? "bg-white/20 text-white" : "bg-amber-100 text-amber-600"
                        }`}>
                            Soon
                        </span>
                    )}
                </button>
            );
        })}
    </div>
);

// ── Audience ─────────────────────────────────────────────────────
export const AUDIENCE_OPTIONS = [
    { value: "all_members", label: "All Members" },
    { value: "village",     label: "Village" },
    { value: "custom",      label: "Custom List" },
];

export const audienceLabel = (value) => AUDIENCE_OPTIONS.find((a) => a.value === value)?.label || value;

const AUDIENCE_STYLES = {
    all_members: "bg-primary/10 text-primary",
    village:     "bg-blue-50 text-blue-600",
    custom:      "bg-purple-50 text-purple-600",
};

export const AudienceBadge = ({ type }) => (
    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full ${AUDIENCE_STYLES[type] || "bg-gray-100 text-gray-500"}`}>
        {audienceLabel(type)}
    </span>
);

// ── Dispatch status — derived from sent_at (sending isn't wired up yet) ──
export const DispatchStatusBadge = ({ sentAt }) => (
    <span className={`inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 rounded-full ${
        sentAt ? "bg-primary/10 text-primary" : "bg-amber-50 text-amber-700"
    }`}>
        <span className={`w-1.5 h-1.5 rounded-full ${sentAt ? "bg-primary" : "bg-amber-500"}`} />
        {sentAt ? "Sent" : "Pending dispatch"}
    </span>
);

// ── Recipient status ─────────────────────────────────────────────
const RECIPIENT_STYLES = {
    pending: "bg-amber-50 text-amber-700",
    sent:    "bg-primary/10 text-primary",
    failed:  "bg-red-50 text-red-600",
};

export const RecipientStatusBadge = ({ status }) => (
    <span className={`inline-flex items-center text-xs font-medium px-2.5 py-1 rounded-full capitalize ${RECIPIENT_STYLES[status] || "bg-gray-100 text-gray-500"}`}>
        {status}
    </span>
);

// ── Sub-nav tabs shared by Broadcasts / Templates pages ─────────────
export const NotificationTabs = ({ active }) => (
    <div className="inline-flex items-center gap-1 bg-white border border-gray-100 shadow-sm rounded-xl p-1">
        <NavLink
            to="/notifications"
            end
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                active === "broadcasts" ? "bg-primary text-white shadow-sm" : "text-gray-500 hover:bg-gray-50"
            }`}
        >
            Sent History
        </NavLink>
        <NavLink
            to="/notifications/templates"
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                active === "templates" ? "bg-primary text-white shadow-sm" : "text-gray-500 hover:bg-gray-50"
            }`}
        >
            Templates
        </NavLink>
    </div>
);

// ── Pagination footer — shared by every table on these pages ────────
export const Pagination = ({ page, totalPages, onPrev, onNext }) => {
    if (totalPages <= 1) return null;
    return (
        <div className="flex items-center justify-between mt-5">
            <p className="text-xs text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex items-center gap-2">
                <button
                    onClick={onPrev}
                    disabled={page === 1}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50"
                >
                    ‹
                </button>
                <button
                    onClick={onNext}
                    disabled={page === totalPages}
                    className="w-8 h-8 flex items-center justify-center rounded-lg border border-gray-200 text-gray-500 disabled:opacity-40 hover:bg-gray-50"
                >
                    ›
                </button>
            </div>
        </div>
    );
};
